import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase, {ButtonBaseProps} from '@mui/material/ButtonBase';
import {SxImg} from '@practicegenius/pg-ui/src/base/Material/components/StyleableBaseComponents';
import {MuiSxProps} from '@practicegenius/pg-ui/src/base/Material/utils/sx-helpers';
import {ServiceBrand} from '@practicegenius/domain/src/data/serviceBrand';
// Using CSS modules here as it's the best way to define the animation parameters in a future-proof way.
import styles from './Home.module.css';
import prhShimmerLogo from '../../../../assets/svg/logos/prh/logo-shimmer.svg';
import hubbuxLogo from '../../../../assets/svg/logos/hubbux/logo.svg';
import culturelloLogo from '../../../../assets/svg/logos/culturello/logo.svg';
import {
  BundledImageAsset,
  getBundledImageAssetProps,
} from '../../../../assets/BundledImageAsset';

const prhLogoAspectRatio = 206 / 144;
const logoMaxWidth = `calc(350px * ${prhLogoAspectRatio})`;
const logoImageLimitSx: MuiSxProps = {
  maxHeight: '350px',
  maxWidth: logoMaxWidth,
};

const brandedLogoSrc: Record<ServiceBrand, BundledImageAsset> = {
  patientrewardshub: prhShimmerLogo,
  hubbux: hubbuxLogo,
  culturello: culturelloLogo,
};

const brandedGradientBackgroundClassname: Record<
  ServiceBrand,
  string | undefined
> = {
  patientrewardshub: styles.gradientBackground,
  hubbux: undefined,
  culturello: undefined,
};

const baseButtonSx: MuiSxProps = {
  width: '100%',
  py: 2,
  textAlign: 'center',
  borderRadius: '100px',
  border: theme => `1px solid ${theme.palette.grey[400]}`,
  color: 'text.secondary',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: theme => theme.palette.grey[100],
  },
  maxWidth: logoMaxWidth,
  alignSelf: 'center',
};

type ButtonProps = Omit<ButtonBaseProps, 'sx' | 'classname'>;
/** This currently only provides home screen of sites-dot that is presented in the mobile app.
 * The web version of the home page is still implemented in Kohana
 */
export const Home = ({
  serviceBrand,
  buttonProps,
}: {
  serviceBrand: ServiceBrand;
  buttonProps: {
    login: ButtonProps;
    register: ButtonProps;
  };
}) => {
  const logoImageProps = getBundledImageAssetProps(
    brandedLogoSrc[serviceBrand]
  );
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        maxHeight: '778px',
      }}
    >
      <Box
        sx={{
          grow: 1,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: '55%',
            mb: 4,
            backgroundColor: 'common.white',
            minHeight: '144px',
            ...logoImageLimitSx,
          }}
        >
          <Box
            /**
             * The PRH logo used here is special: it has a white background and the hub lettering is transparent.
             * By reusing the animated gradient background from the login button, we can achieve the same effect on the logo.
             * Some day it will be possible to use css gradients in SVG, but for now we have to use this workaround.
             * @see https://svgwg.org/svg2-draft/single-page.html
             */
            className={brandedGradientBackgroundClassname[serviceBrand]}
          >
            <SxImg
              sx={{
                width: '100%',
                ...logoImageLimitSx,
                /**
                 * Make the image slightly larger than the container to avoid a subpixel rendering bug
                 * @see https://practicegenius.atlassian.net/browse/PAT-12831
                 */
                transform: 'scale(1.01)',
                // other styles needed to ensure that gradient background is hidden behind the background of the image
                verticalAlign: 'middle',
              }}
              alt="Hub Logo"
              src={logoImageProps.src}
            />
          </Box>
        </Box>
        <Typography
          sx={{
            fontSize: '37px',
            fontWeight: 600,
            lineHeight: 1.3,
            color: 'common.black',
          }}
        >
          Get Rewarded!
        </Typography>
        <Typography variant="subtitle1" color="secondary">
          Earn, redeem, build good habits!
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          px: 2,
          gap: 2,
          mb: 8,
        }}
      >
        <ButtonBase
          {...buttonProps.login}
          sx={{
            ...baseButtonSx,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            border: 0,
          }}
          className={brandedGradientBackgroundClassname[serviceBrand]}
        >
          Log In
        </ButtonBase>
        <ButtonBase {...buttonProps.register} sx={baseButtonSx}>
          Register New Account
        </ButtonBase>
      </Box>
    </Box>
  );
};
